import { Controller } from "@hotwired/stimulus"
import { patch } from "@rails/request.js"

// GOAT SPAM Protector flag toast (#14946, spec §12a), modelled on the blog
// toast (acknowledge_toast_controller, #15794). Rides on the same element as
// gl-toast, which owns the look and lifecycle; this controller only records
// that the owner SAW the flag, server-side, so it never comes back on any
// browser or device.
//
// "Seen" is an explicit act: the close button, Escape (gl-toast's keyboard
// dismiss), or "View". Merely rendering is not seen: until one of those
// happens the server keeps rendering the toast on every page load.
//
// "View" navigates in the SAME tab, which would cancel an in-flight PATCH, so
// a plain left click waits for the acknowledgement (success or failure) and
// then navigates. A modified or middle click opens a new tab and is left to
// the browser. If the PATCH fails the toast simply returns on the next page
// load — the safe direction for a "make sure it is seen" rule.
export default class extends Controller {
  static values = { acknowledgeUrl: String }

  connect() {
    this.element.addEventListener("click", this.onClick)
    this.element.addEventListener("auxclick", this.onAuxClick)
    this.element.addEventListener("keydown", this.onKeydown)
  }

  disconnect() {
    this.element.removeEventListener("click", this.onClick)
    this.element.removeEventListener("auxclick", this.onAuxClick)
    this.element.removeEventListener("keydown", this.onKeydown)
  }

  onClick = (event) => {
    if (event.target.closest(".gl-toast-close")) {
      this.acknowledge()
      return
    }
    const link = event.target.closest("[data-spam-protector-toast-view]")
    if (!link) return

    if (this.opensNewTab(event)) {
      this.acknowledge()
      this.dismissToast()
      return
    }
    event.preventDefault()
    this.dismissToast()
    this.acknowledge().finally(() => this.visit(link.getAttribute("href")))
  }

  onAuxClick = (event) => {
    if (event.button === 1 && event.target.closest("[data-spam-protector-toast-view]")) {
      this.acknowledge()
      this.dismissToast()
    }
  }

  onKeydown = (event) => {
    if (event.key === "Escape") this.acknowledge()
  }

  opensNewTab(event) {
    return event.metaKey || event.ctrlKey || event.shiftKey || event.button !== 0
  }

  acknowledge() {
    if (!this.acknowledgement) {
      this.acknowledgement = this.acknowledgeUrlValue
        ? patch(this.acknowledgeUrlValue, { responseKind: "json" }).catch(() => {})
        : Promise.resolve()
    }
    return this.acknowledgement
  }

  visit(href) {
    window.location.assign(href)
  }

  dismissToast() {
    this.application.getControllerForElementAndIdentifier(this.element, "gl-toast")?.dismiss()
  }
}
